// Pure comp-selection logic for the Pricing Strategy modal (kept out of
// StrategyPricingModal.jsx so it can be unit tested - that component's
// import chain needs Firebase, and this repo's react/react-dom version
// mismatch breaks @testing-library rendering).
import { computeCompSetSignature } from "./compSetSignature";

/**
 * The verified (structured) comps actually sent for valuation: the agent's
 * included Datafiniti comps plus any review candidate the agent explicitly
 * included with a reason (agentReview). Shared by generation and by the
 * stale-recommendation check so both describe the same set.
 */
export function buildSelectedVerifiedComps(compList, excludedIndices, reviewList, reviewInclusions) {
  const selected = (compList || [])
    .filter((_, idx) => !excludedIndices.has(idx))
    .map((c) => ({ ...c, source: "Web Search", isStructured: true }));
  const included = (reviewList || [])
    .filter((c) => reviewInclusions.has(c.address))
    .map((c) => ({ ...c, source: "Web Search", isStructured: true, agentReview: { included: true, reason: reviewInclusions.get(c.address) } }));
  return [...selected, ...included];
}

/** Restores excluded comp indices from the selections stored with a result. */
export function excludedIndicesFromSelections(compList, compSelections) {
  const excluded = new Set(compSelections?.excludedAddresses || []);
  return new Set((compList || []).map((c, idx) => (excluded.has(c.address) ? idx : -1)).filter((idx) => idx >= 0));
}

/** The audit trail of the agent's comp decisions stored with a result. */
export function buildCompSelections(compList, excludedIndices, reviewList, reviewInclusions) {
  return {
    excludedAddresses: (compList || []).filter((_, idx) => excludedIndices.has(idx)).map((c) => c.address),
    includedReviewCandidates: (reviewList || [])
      .filter((c) => reviewInclusions.has(c.address))
      .map((c) => ({ address: c.address, reason: reviewInclusions.get(c.address) })),
  };
}

/**
 * A result is stale when it was calculated from a different verified comp
 * set than the one currently selected. Results generated before signatures
 * existed fall back to "the agent changed the selection this session".
 * Without any comp data on hand nothing can be compared, so nothing is
 * claimed either way.
 */
export function isResultStale({ result, compList, excludedIndices, reviewList, reviewInclusions, selectionChanged }) {
  if (!result) return false;
  if (!(compList || []).length && !(reviewList || []).length) return false;
  if (!result.compSetSignature) return Boolean(selectionChanged);
  const current = computeCompSetSignature(buildSelectedVerifiedComps(compList, excludedIndices, reviewList, reviewInclusions));
  return current !== result.compSetSignature;
}
